<template>
    <v-container fluid class="page-commesse">

        <!-- Header -->
        <div class="page-header">
            <div class="header-title">
                <v-icon size="22" color="primary">mdi-clipboard-list-outline</v-icon>
                <div class="titles">
                    <h1>Commesse</h1>
                    <p class="subtitle">Rilievi misure e dati commessa</p>
                </div>
            </div>
            <v-btn color="primary" size="large" prepend-icon="mdi-plus" @click="nuovoOrdine">
                Nuova commessa
            </v-btn>
        </div>

        <!-- KPI -->
        <v-row class="kpi-row" dense>
            <v-col cols="6" md="3">
                <v-card class="kpi-card kpi-card--primary elevation-1">
                    <div class="kpi-top"><v-icon color="primary">mdi-view-list</v-icon></div>
                    <div class="kpi-value">{{ ordini.length }}</div>
                    <div class="kpi-label">Commesse totali</div>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="secondary">mdi-calendar-today</v-icon></div>
                    <div class="kpi-value">{{ ordiniCreatiOggi }}</div>
                    <div class="kpi-label">Create oggi</div>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="secondary">mdi-calendar-week</v-icon></div>
                    <div class="kpi-value">{{ ordiniUltimi7Giorni }}</div>
                    <div class="kpi-label">Ultimi 7 giorni</div>
                </v-card>
            </v-col>
            <v-col cols="6" md="3">
                <v-card class="kpi-card elevation-1">
                    <div class="kpi-top"><v-icon color="secondary">mdi-account-multiple</v-icon></div>
                    <div class="kpi-value">{{ clientiUnici }}</div>
                    <div class="kpi-label">Clienti unici</div>
                </v-card>
            </v-col>
        </v-row>

        <v-card class="comp-elenco elevation-2">

            <!-- Toolbar / Filtri -->
            <div class="toolbar">
                <div class="toolbar-row">
                    <v-text-field v-model="ricerca" placeholder="Cerca commessa, cliente o cantiere..."
                        variant="outlined" density="compact" clearable hide-details>
                        <template #prepend-inner><v-icon>mdi-magnify</v-icon></template>
                    </v-text-field>

                    <v-autocomplete v-model="filtroCliente" :items="clienti" label="Cliente" variant="outlined"
                        density="compact" clearable hide-details class="flt">
                        <template #prepend-inner><v-icon>mdi-account</v-icon></template>
                    </v-autocomplete>

                    <v-autocomplete v-model="filtroCantiere" :items="cantieri" label="Cantiere" variant="outlined"
                        density="compact" clearable hide-details class="flt">
                        <template #prepend-inner>
                            <v-icon>mdi-home-city-outline</v-icon>
                        </template>
                    </v-autocomplete>

                    <v-text-field v-model="dal" label="Dal" type="date" variant="outlined" hide-details class="date"
                        density="compact" />
                    <v-text-field v-model="al" label="Al" type="date" variant="outlined" hide-details class="date"
                        density="compact" />

                    <v-btn class="flt" variant="tonal" color="secondary" @click="resetFiltri"
                        prepend-icon="mdi-filter-remove">
                        Reset
                    </v-btn>
                    <v-btn icon @click="caricaOrdini"><v-icon>mdi-refresh</v-icon></v-btn>
                </div>

                <div class="quick-filters">
                    <span>Veloci:</span>
                    <v-chip-group selected-class="chip-active" column>
                        <v-chip size="small" @click="rangeOggi" variant="outlined"
                            prepend-icon="mdi-flash">Oggi</v-chip>
                        <v-chip size="small" @click="rangeSettimana" variant="outlined"
                            prepend-icon="mdi-calendar-week">Questa
                            settimana</v-chip>
                        <v-chip size="small" @click="rangeMese" variant="outlined"
                            prepend-icon="mdi-calendar-month">Questo
                            mese</v-chip>
                    </v-chip-group>
                </div>

                <!-- Chips filtri attivi -->
                <div class="active-filters" v-if="hasActiveFilters">
                    <v-chip v-if="ricerca" size="small" closable @click:close="ricerca = ''"
                        prepend-icon="mdi-magnify">{{ ricerca
                        }}</v-chip>
                    <v-chip v-if="filtroCliente" size="small" closable @click:close="filtroCliente = null"
                        prepend-icon="mdi-account">
                        Cliente: {{ filtroCliente }}
                    </v-chip>
                    <v-chip v-if="filtroCantiere" size="small" closable @click:close="filtroCantiere = null"
                        prepend-icon="mdi-home-city-outline">
                        Cantiere: {{ filtroCantiere }}
                    </v-chip>
                    <v-chip v-if="dal || al" size="small" closable @click:close="dal = null; al = null"
                        prepend-icon="mdi-calendar-range">
                        {{ labelRange }}
                    </v-chip>
                </div>
            </div>

            <!-- Skeleton caricamento -->
            <template v-if="loading || loadingConfig">
                <div class="skeleton-wrap">
                    <v-skeleton-loader type="table" class="elevation-0" />
                </div>
            </template>

            <!-- Tabella dinamica -->
            <v-data-table v-else :headers="headersVisibili" :items="ordiniFiltrati" fixed-header hover
                class="elenco-table" @click:row="apriDettaglio" no-data-text="Nessun dato disponibile"
                :items-per-page="-1" hide-default-footer>

                <!-- Numero commessa sempre bold -->
                <template v-slot:[`item.numeroCommessa`]="{ item }">
                    <span class="commessa">{{ item.numeroCommessa }}</span>
                </template>

                <!-- Formattazione date dinamica per qualsiasi colonna di tipo date -->
                <template v-for="col in colonneData" :key="col.value" v-slot:[`item.${col.value}`]="{ item }">
                    {{ formatDate(item[col.value]) }}
                </template>

                <!-- Stato vuoto -->
                <template v-slot:no-data>
                    <div class="empty-state">
                        <v-icon color="primary" size="36">mdi-archive-alert-outline</v-icon>
                        <div class="empty-title">Nessuna commessa trovata</div>
                        <div class="empty-text">
                            Prova a cambiare o azzerare i filtri di ricerca.
                        </div>
                        <v-btn variant="tonal" color="primary" @click="resetFiltri" prepend-icon="mdi-filter-remove">
                            Reset filtri
                        </v-btn>
                    </div>
                </template>

                <!-- Azioni (sempre visibili, non configurabili) -->
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex align-center justify-end gap-1">
                        <v-btn icon variant="text" color="success" size="small" :loading="esportando === item.id"
                            @click.stop="esportaExcel(item)" title="Esporta Excel">
                            <v-icon>mdi-microsoft-excel</v-icon>
                        </v-btn>
                        <v-btn icon variant="text" color="error" size="small" @click.stop="eliminaOrdine(item)">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>

            </v-data-table>
        </v-card>
    </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { get_elenco, elimina_ordine, esporta_commessa_excel } from '@/api/api.misure.js'
import { get_configurazione } from '@/api/config/api.campi.js'

// ── Mappa CampoKey → property dell'oggetto ordine (camelCase) ─────────────────
// I nomi delle colonne nel backend tornano in camelCase via JSON serialization.
const CAMPO_KEY_TO_PROP = {
    NumeroCommessa: 'numeroCommessa',
    TipoCommessa: 'tipoCommessa',
    Data: 'data',
    DataConsegna: 'dataConsegna',
    CognomeCliente: 'cognomeCliente',
    NomeCliente: 'nomeCliente',
    RagioneSociale: 'ragioneSociale',
    ViaCliente: 'viaCliente',
    CittaCliente: 'cittaCliente',
    CapCliente: 'capCliente',
    EmailCliente: 'emailCliente',
    TelefonoCliente: 'telefonoCliente',
    PiCfCliente: 'piCfCliente',
    IntestazioneCantiere: 'intestazioneCantiere',
    ViaCantiere: 'viaCantiere',
    CittaCantiere: 'cittaCantiere',
    Essenza: 'essenza',
    DescrizioneEssenza: 'descrizioneEssenza',
    Serie: 'serie',
    Profilo: 'profilo',
    Spessore: 'spessore',
    Verniciatura: 'verniciatura',
    Persiana: 'persiana',
    Avvolgibili: 'avvolgibili',
    Antone: 'antone',
    ZanzarieraTipo: 'zanzarieraTipo',
    ZanzarieraColore: 'zanzarieraColore',
    Cassonetto: 'cassonetto',
    Maniglia: 'maniglia',
    Vetro: 'vetro',
}

// Colonne fisse sempre presenti (non configurabili)
const COLONNE_FISSE_INIZIO = [
    { title: 'N. Commessa', value: 'numeroCommessa', sortable: true, width: '130' },
]
const COLONNE_FISSE_FINE = [
    { title: '', value: 'actions', sortable: false, width: '90', align: 'end' },
]

// Colonne di tipo data (per applicare formatDate nel template)
const CAMPI_DATA = ['Data', 'DataConsegna']

export default {
    name: 'ElencoCommesse',

    setup() {
        const router = useRouter()
        const loading = ref(false)
        const loadingConfig = ref(true)
        const ordini = ref([])
        const ricerca = ref('')
        const filtroCliente = ref(null)
        const filtroCantiere = ref(null)
        const dal = ref(null)
        const al = ref(null)
        const esportando = ref(null)

        // Campi configurabili con visibileTabella = true
        const campiConfigurati = ref([])  // array di CfgCampoAziendaDto

        // ── Colonne tabella dinamiche ───────────────────────────────────────
        const headersVisibili = computed(() => {
            const colonne = campiConfigurati.value
                // Solo campi con visibileTabella attivo e non la commessa (già fissa)
                .filter(c =>
                    c.visibileTabella &&
                    !c.campoKey.startsWith('misura_') &&
                    c.campoKey !== 'NumeroCommessa'
                )
                .map(c => ({
                    title: c.etichetta,
                    value: CAMPO_KEY_TO_PROP[c.campoKey] || c.campoKey,
                    sortable: true,
                    width: larghezzaColonna(c),
                    _campoKey: c.campoKey  // tengo per il check tipo data
                }))

            return [
                ...COLONNE_FISSE_INIZIO,
                ...colonne,
                ...COLONNE_FISSE_FINE
            ]
        })

        // Lista dei value delle colonne di tipo data (per il template v-for slot)
        const colonneData = computed(() =>
            headersVisibili.value.filter(h => CAMPI_DATA.includes(h._campoKey))
        )

        // ── Caricamento configurazione ──────────────────────────────────────
        const caricaConfig = async () => {
            loadingConfig.value = true
            try {
                const res = await get_configurazione('serramenti')
                const sezioni = res.data?.sezioni || []
                // Appiattisco tutti i campi da tutte le sezioni
                campiConfigurati.value = sezioni.flatMap(s => s.campi)
            } catch (e) {
                console.error('Errore caricamento config colonne:', e)
            } finally {
                loadingConfig.value = false
            }
        }

        // ── Caricamento ordini ──────────────────────────────────────────────
        const caricaOrdini = async () => {
            loading.value = true
            try {
                const response = await get_elenco()
                ordini.value = response?.data || response || []
            } catch (e) {
                console.error('Errore caricamento commesse:', e)
            }
            loading.value = false
        }

        // ── Filtri ─────────────────────────────────────────────────────────
        const clienti = computed(() => {
            const set = new Set((ordini.value || []).map(o => o.cognomeCliente).filter(Boolean))
            return Array.from(set).sort((a, b) => a.localeCompare(b))
        })

        const cantieri = computed(() => {
            const set = new Set(
                (ordini.value || []).map(o => o.intestazioneCantiere).filter(Boolean)
            )
            return Array.from(set).sort((a, b) => a.localeCompare(b))
        })

        const ordiniFiltrati = computed(() => {
            const q = (ricerca.value || '').trim().toLowerCase()
            const start = dal.value ? new Date(dal.value) : null
            const end = al.value ? new Date(al.value) : null
            if (end) end.setHours(23, 59, 59, 999)

            return (ordini.value || []).filter(o => {
                const testoOK = !q || [
                    o.numeroCommessa, o.cognomeCliente, o.intestazioneCantiere
                ].some(v => (v || '').toString().toLowerCase().includes(q))

                const clienteOK = !filtroCliente.value ||
                    o.cognomeCliente === filtroCliente.value
                const cantiereOK = !filtroCantiere.value ||
                    o.intestazioneCantiere === filtroCantiere.value

                const dataOK = (() => {
                    if (!start && !end) return true
                    if (!o.data) return false
                    const d = new Date(o.data)
                    if (start && d < start) return false
                    if (end && d > end) return false
                    return true
                })()

                return testoOK && clienteOK && cantiereOK && dataOK
            })
        })

        // ── KPI ────────────────────────────────────────────────────────────
        const ordiniCreatiOggi = computed(() => {
            const oggi = toISODate(new Date())
            return (ordini.value || []).filter(o => o.data?.startsWith(oggi)).length
        })

        const ordiniUltimi7Giorni = computed(() => {
            const end = new Date()
            const start = new Date()
            start.setDate(end.getDate() - 6)
            start.setHours(0, 0, 0, 0)
            end.setHours(23, 59, 59, 999)
            return (ordini.value || []).filter(o => {
                if (!o.data) return false
                const d = new Date(o.data)
                return d >= start && d <= end
            }).length
        })

        const clientiUnici = computed(() =>
            new Set((ordini.value || []).map(o => o.cognomeCliente).filter(Boolean)).size
        )

        // ── Filtri attivi ──────────────────────────────────────────────────
        const hasActiveFilters = computed(() =>
            !!(ricerca.value || filtroCliente.value || filtroCantiere.value ||
                dal.value || al.value)
        )

        const labelRange = computed(() => {
            if (!dal.value && !al.value) return ''
            const da = dal.value ? new Date(dal.value).toLocaleDateString('it-IT') : '…'
            const a = al.value ? new Date(al.value).toLocaleDateString('it-IT') : '…'
            return `Dal ${da} al ${a}`
        })

        // ── Navigazione ────────────────────────────────────────────────────
        const nuovoOrdine = () => {
            router.push({ name: 'DettaglioOrdine', params: { id: 'nuovo' } })
        }

        const apriDettaglio = (event, { item }) => {
            router.push({ name: 'DettaglioOrdine', params: { id: item.id } })
        }

        // ── Elimina ────────────────────────────────────────────────────────
        const eliminaOrdine = async (ordine) => {
            if (!confirm(`Eliminare la commessa ${ordine.numeroCommessa}?`)) return
            try {
                await elimina_ordine(ordine.id)
                caricaOrdini()
            } catch (e) {
                console.error('Errore eliminazione commessa:', e)
            }
        }

        // ── Export Excel ───────────────────────────────────────────────────
        const esportaExcel = async (ordine) => {
            esportando.value = ordine.id
            try {
                const response = await esporta_commessa_excel(ordine.id)
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download',
                    `Commessa_${ordine.numeroCommessa.replace('/', '-')}.xlsx`)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error('Errore export Excel:', e)
            } finally {
                esportando.value = null
            }
        }

        // ── Quick range ────────────────────────────────────────────────────
        const resetFiltri = () => {
            ricerca.value = ''
            filtroCliente.value = null
            filtroCantiere.value = null
            dal.value = null
            al.value = null
        }
        const rangeOggi = () => { const iso = toISODate(new Date()); dal.value = iso; al.value = iso }
        const rangeSettimana = () => {
            const end = new Date()
            const start = startOfWeek(end)
            dal.value = toISODate(start); al.value = toISODate(end)
        }
        const rangeMese = () => {
            const end = new Date()
            const start = new Date(end.getFullYear(), end.getMonth(), 1)
            dal.value = toISODate(start); al.value = toISODate(end)
        }

        // ── Utils ──────────────────────────────────────────────────────────
        const formatDate = (date) => {
            if (!date) return '—'
            return new Date(date).toLocaleDateString('it-IT')
        }

        const larghezzaColonna = (campo) => {
            if (campo.tipoInput === 'date') return '110'
            if (campo.tipoInput === 'checkbox') return '80'
            if (['CognomeCliente', 'RagioneSociale', 'IntestazioneCantiere'].includes(campo.campoKey))
                return undefined  // auto
            return '130'
        }

        function toISODate(d) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        }

        function startOfWeek(d) {
            const date = new Date(d)
            const day = date.getDay() || 7
            if (day !== 1) date.setHours(-24 * (day - 1))
            date.setHours(0, 0, 0, 0)
            return date
        }

        onMounted(async () => {
            // Carico config e ordini in parallelo
            await Promise.all([caricaConfig(), caricaOrdini()])
        })

        return {
            loading, loadingConfig,
            ordini, ordiniFiltrati,
            ricerca, filtroCliente, filtroCantiere, clienti, cantieri,
            dal, al, hasActiveFilters, labelRange,
            headersVisibili, colonneData,
            ordiniCreatiOggi, ordiniUltimi7Giorni, clientiUnici,
            esportando,
            nuovoOrdine, apriDettaglio,
            eliminaOrdine, esportaExcel,
            formatDate,
            resetFiltri, rangeOggi, rangeSettimana, rangeMese,
            caricaOrdini,
        }
    }
}
</script>

<style scoped>
.page-commesse {
    margin: auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 16px;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.titles h1 {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: #1E3A5F;
}

.subtitle {
    margin: 0;
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
}

/* KPI */
.kpi-row {
    margin-bottom: 12px;
}

.kpi-card {
    padding: 14px 14px 12px;
    border-radius: 14px;
    position: relative;
    background: white;
    border: 1px solid #eef0f3;
}

.kpi-card--primary {
    box-shadow: 0 6px 18px rgba(30, 58, 95, 0.08);
    border: 1px solid rgba(30, 58, 95, 0.12);
}

.kpi-top {
    position: absolute;
    right: 12px;
    top: 10px;
    opacity: .8;
}

.kpi-value {
    font-size: 22px;
    font-weight: 800;
    color: #1E3A5F;
}

.kpi-label {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
}

/* Toolbar */
.toolbar {
    padding: 12px;
    border-bottom: 1px solid #eee;
    background: #F9FAFB;
}

.toolbar-row {
    display: grid;
    gap: 10px;
    grid-template-columns: 1.3fr 1fr 1fr 150px 150px auto auto;
    align-items: center;
}

.flt {
    min-width: 110px;
}

.date {
    min-width: 140px;
}

.date :deep(.v-field__input) {
    padding-inline-start: 8px !important;
}

.quick-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
}

.quick-filters span {
    font-size: 12px;
    color: #6b7280;
}

.chip-active {
    background: rgba(245, 158, 11, 0.15) !important;
    color: #9a6a02;
}

.active-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 10px;
}

/* Tabella */
.elenco-table {
    font-size: .93rem;
}

.elenco-table :deep(thead th) {
    background: #f6f7f9 !important;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.elenco-table :deep(tbody tr:hover) {
    background: #f0f4f8 !important;
    cursor: pointer;
}

.commessa {
    font-weight: 700;
    color: #1E3A5F;
}

.skeleton-wrap {
    padding: 12px;
}

.empty-state {
    padding: 28px 12px;
    text-align: center;
    color: #6b7280;
    display: grid;
    gap: 8px;
    justify-items: center;
}

.empty-title {
    font-weight: 700;
    color: #1E3A5F;
}
</style>